import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Ticket, PartyPopper, Clock } from 'lucide-react';
import SlotMachineRoulette from './SlotMachineRoulette';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generate WebSocket URL from backend URL
const getWebSocketUrl = (drawId) => {
  const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
  const baseUrl = BACKEND_URL.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${baseUrl}/ws/live-draw/${drawId}`;
};

// Generate random values outside the component to avoid lint errors
const generateParticleAnimations = () => 
  [...Array(20)].map((_, i) => ({
    x: (i * 25) % 500,
    duration: 3 + (i % 3),
    delay: (i * 0.1) % 2
  }));

const PARTICLE_ANIMATIONS = generateParticleAnimations();

const LiveDrawOverlay = ({ drawId, userId, onClose }) => {
  const [phase, setPhase] = useState('countdown'); // countdown, spinning, winner
  const [countdown, setCountdown] = useState(10);
  const [drawData, setDrawData] = useState(null);
  const [isWinner, setIsWinner] = useState(false);
  const [spinComplete, setSpinComplete] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Connect to WebSocket
  useEffect(() => {
    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(getWebSocketUrl(drawId));
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('WebSocket connected for live draw');
          setWsConnected(true);
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log('WebSocket message:', message);

            switch (message.type) {
              case 'status':
                // Initial status message
                setDrawData(message);
                if (message.status === 'countdown' && message.draw_started_at) {
                  const startTime = new Date(message.draw_started_at).getTime();
                  const now = Date.now();
                  const elapsed = Math.floor((now - startTime) / 1000);
                  const remaining = Math.max(0, 10 - elapsed);
                  setCountdown(remaining);
                  if (remaining <= 0) {
                    setPhase('spinning');
                  }
                } else if (message.status === 'completed') {
                  setPhase('winner');
                  setIsWinner(message.winning_ticket && userId === message.winner_id);
                }
                break;

              case 'countdown_started':
                setDrawData(prev => ({ ...prev, ...message }));
                setPhase('countdown');
                setCountdown(10);
                break;

              case 'spinning_started':
                setDrawData(prev => ({ 
                  ...prev, 
                  status: 'spinning',
                  winning_ticket: message.winning_ticket 
                }));
                setPhase('spinning');
                break;

              case 'draw_completed':
                setDrawData(prev => ({ 
                  ...prev, 
                  status: 'completed',
                  winner_name: message.winner_name,
                  winning_ticket: message.winning_ticket 
                }));
                // Don't immediately set winner phase - let the animation complete
                break;

              case 'ping':
                // Server ping, send pong
                ws.send('ping');
                break;

              case 'pong':
                // Server responded to our ping
                break;

              default:
                console.log('Unknown WebSocket message type:', message.type);
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          setWsConnected(false);
        };

        ws.onclose = () => {
          console.log('WebSocket closed');
          setWsConnected(false);
          // Attempt to reconnect after 3 seconds
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
        };
      } catch (error) {
        console.error('Error connecting to WebSocket:', error);
        // Fallback: retry connection
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [drawId, userId]);

  // Countdown timer
  useEffect(() => {
    if (phase === 'countdown' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setPhase('spinning');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [phase, countdown]);

  // Handle spin complete
  const handleSpinComplete = useCallback(() => {
    setSpinComplete(true);
    // Wait a moment then show winner
    setTimeout(() => {
      setPhase('winner');
      // Check if current user is the winner
      if (drawData?.winning_ticket) {
        // Trigger confetti regardless
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }
    }, 1500);
  }, [drawData]);

  // Trigger extra confetti for winner
  useEffect(() => {
    if (phase === 'winner' && isWinner) {
      const duration = 3000;
      const end = Date.now() + duration;
      
      const frame = () => {
        confetti({
          particleCount: 7,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#EAB308', '#A855F7', '#10b981']
        });
        confetti({
          particleCount: 7,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#EAB308', '#A855F7', '#10b981']
        });
        
        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };
      frame();
    }
  }, [phase, isWinner]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.8, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          className="bg-[#18181b] border-2 border-[#EAB308] rounded-3xl p-8 max-w-lg w-full text-center relative overflow-hidden"
        >
          {/* Background animation */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {PARTICLE_ANIMATIONS.map((particle, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-[#EAB308]/20 rounded-full"
                initial={{ 
                  x: particle.x, 
                  y: -20,
                  opacity: 0.5
                }}
                animate={{ 
                  y: 600,
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: particle.duration,
                  repeat: Infinity,
                  delay: particle.delay
                }}
              />
            ))}
          </div>

          {/* Header */}
          <div className="relative z-10">
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
              className="inline-flex items-center gap-2 bg-[#EAB308]/20 px-4 py-2 rounded-full mb-4"
            >
              <Ticket className="w-5 h-5 text-[#EAB308]" />
              <span className="text-[#EAB308] font-bold">LIVE DRAW</span>
              {wsConnected && (
                <span className="w-2 h-2 bg-[#10b981] rounded-full animate-pulse" title="Connected" />
              )}
            </motion.div>
            
            <h2 className="text-2xl font-heading font-bold text-white mb-2">
              {drawData?.title || 'Loading...'}
            </h2>
            <p className="text-[#a1a1aa] mb-6">
              Prize: <span className="text-[#EAB308]">{drawData?.prize || '...'}</span>
            </p>
          </div>

          {/* Countdown Phase */}
          {phase === 'countdown' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative z-10"
            >
              <div className="flex items-center justify-center gap-2 mb-4">
                <Clock className="w-6 h-6 text-[#a1a1aa]" />
                <span className="text-[#a1a1aa]">Draw starting in...</span>
              </div>
              <motion.div
                key={countdown}
                initial={{ scale: 1.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-8xl font-heading font-bold text-[#EAB308] mb-4"
              >
                {countdown}
              </motion.div>
              <p className="text-sm text-[#52525b]">
                {drawData?.ticket_count || 0} tickets in this draw
              </p>
            </motion.div>
          )}

          {/* Spinning Phase */}
          {phase === 'spinning' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative z-10"
            >
              <SlotMachineRoulette
                tickets={drawData?.all_tickets || []}
                winningTicket={drawData?.winning_ticket}
                isSpinning={!spinComplete}
                onSpinComplete={handleSpinComplete}
              />
            </motion.div>
          )}

          {/* Winner Phase */}
          {phase === 'winner' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative z-10"
            >
              {isWinner ? (
                <>
                  <motion.div
                    animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
                    transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
                  >
                    <PartyPopper className="w-20 h-20 text-[#EAB308] mx-auto mb-4" />
                  </motion.div>
                  <h3 className="text-4xl font-heading font-bold gold-gradient mb-4">
                    YOU WON!
                  </h3>
                  <div className="bg-[#EAB308] text-black font-mono text-3xl font-bold px-8 py-4 rounded-xl inline-block mb-4">
                    #{drawData?.winning_ticket}
                  </div>
                  <p className="text-[#a1a1aa]">
                    Congratulations! You will receive instructions on how to claim your prize.
                  </p>
                </>
              ) : (
                <>
                  <Trophy className="w-16 h-16 text-[#EAB308] mx-auto mb-4" />
                  <h3 className="text-2xl font-heading font-bold text-white mb-2">
                    Winner Announced!
                  </h3>
                  <div className="bg-[#27272a] rounded-xl p-4 mb-4">
                    <p className="text-sm text-[#a1a1aa] mb-2">Winning Ticket</p>
                    <div className="text-[#EAB308] font-mono text-2xl font-bold">
                      #{drawData?.winning_ticket}
                    </div>
                  </div>
                  <p className="text-[#a1a1aa]">
                    Congratulations to <span className="text-white font-semibold">{drawData?.winner_name}</span>!
                  </p>
                  <p className="text-sm text-[#52525b] mt-2">
                    Better luck next time! Stay tuned for more draws.
                  </p>
                </>
              )}
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="mt-6 bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold px-8 py-3 rounded-full"
              >
                {isWinner ? 'Awesome! 🎉' : 'Close'}
              </motion.button>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LiveDrawOverlay;
